import React from 'react'
import { connect, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { selectFullscreen } from '@store/actionSlices/appConfig'
import { RootStateTypeExtra } from '@store/types'

import { PropertyView } from '@api/white-label'

import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import {
  CloseSvg,
  CompareSvg,
  EyeSlashSvg,
  EyeSvg,
  FullScreenSvg,
  LinkSvg,
  PanoramaSvg,
  PlaySvg,
  StopCompareSvg,
  StopSvg,
} from '@svg/react'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { PropertyType } from '@adUtilities/types'
import { Unit } from '@adUtilities/types/apartment'

interface ComponentProps {
  unit: Unit
  hasPanoramicData: boolean
  setFullScreenToggle: (arg: boolean) => void
  unitInfoToggle: boolean
  setUnitInfoToggle: (arg: boolean) => void
  slideShowToggle: boolean
  setSlideShowToggle: (arg: boolean) => void
  externalLinkToggle: boolean
  showExternalLinks: boolean
  setShowExternalLinks: (arg: boolean) => void
  isVideoPlaying: boolean
  shouldShowSlideshowBtn: boolean
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  propertyViewWhiteLabel: PropertyView
  hideCompareAction?: boolean
}

function TopControlBar({
  unit,
  hasPanoramicData,
  setFullScreenToggle,
  unitInfoToggle,
  setUnitInfoToggle,
  slideShowToggle,
  setSlideShowToggle,
  externalLinkToggle,
  showExternalLinks,
  setShowExternalLinks,
  isVideoPlaying,
  shouldShowSlideshowBtn,
  comparePropertyProps,
  hideCompareAction,
  propertyViewWhiteLabel,
}: ComponentProps) {
  const fullscreen = useSelector(selectFullscreen)
  const {
    checkPropertyComparability,
    compareModeIsActive,
    startComparison,
    stopComparison,
  } = comparePropertyProps

  const isComparable = React.useMemo<boolean>(
    () =>
      !hideCompareAction &&
      checkPropertyComparability({
        propertyType: PropertyType.Unit,
        data: unit,
      }),
    [unit, checkPropertyComparability, hideCompareAction]
  )

  const handleToggleCompareMode = React.useCallback(() => {
    if (!compareModeIsActive) {
      startComparison({
        property: {
          propertyType: PropertyType.Unit,
          data: unit,
          references: {
            levelId: unit.metas.level,
          },
        },
      })
      return
    }
    stopComparison()
  }, [compareModeIsActive, startComparison, stopComparison, unit])

  return (
    <div className="flex items-center justify-between text-xs font-semibold tracking-widest text-white">
      <button
        type="button"
        onClick={() => setFullScreenToggle(!fullscreen)}
        className="rounded bg-[#747474] p-2"
      >
        {fullscreen ? (
          <CloseSvg className="h-6 w-6 text-white" />
        ) : (
          <FullScreenSvg className="h-6 w-6 text-white" />
        )}
      </button>

      <div className="flex gap-4">
        {!isVideoPlaying && (
          <>
            {externalLinkToggle && !compareModeIsActive && !slideShowToggle && (
              <button
                type="button"
                onClick={() => setShowExternalLinks(!showExternalLinks)}
                className="inline-flex items-center rounded bg-[#747474] px-3 py-1 uppercase"
              >
                <LinkSvg className="mr-1 h-4 w-4" />
                {showExternalLinks
                  ? propertyViewWhiteLabel.closeExternalLinkButtonTitle
                  : propertyViewWhiteLabel.loadExternalLinkButtonTitle}
              </button>
            )}

            {!slideShowToggle && (
              <button
                type="button"
                onClick={() => setUnitInfoToggle(!unitInfoToggle)}
                className="inline-flex items-center rounded bg-[#747474] px-3 py-1 uppercase"
              >
                {unitInfoToggle ? (
                  <>
                    <EyeSlashSvg className="mr-1 h-4 w-4" /> Hide Info
                  </>
                ) : (
                  <>
                    <EyeSvg className="mr-1 h-4 w-4" /> Show Info
                  </>
                )}
              </button>
            )}

            {isComparable && !slideShowToggle && (
              <button
                type="button"
                className="inline-flex items-center rounded bg-[#747474] px-3 py-1 uppercase"
                onClick={handleToggleCompareMode}
              >
                {compareModeIsActive ? (
                  <StopCompareSvg
                    className="mr-1 h-4 w-4 fill-white stroke-white"
                    stroke={1.6}
                  />
                ) : (
                  <CompareSvg
                    className="mr-1 h-4 w-4 stroke-white"
                    stroke={1.6}
                  />
                )}{' '}
                {compareModeIsActive ? 'Stop Comparing' : 'Compare'}
              </button>
            )}

            {hasPanoramicData && !slideShowToggle && !compareModeIsActive && (
              <Link
                to="panoramic"
                className="inline-flex items-center rounded bg-[#747474] px-3 py-1 uppercase"
              >
                <PanoramaSvg className="mr-1 h-5 w-5" />{' '}
                {propertyViewWhiteLabel.panoramicButtonTitle}
              </Link>
            )}

            {shouldShowSlideshowBtn && !compareModeIsActive && (
              <button
                type="button"
                className="inline-flex items-center rounded bg-[#747474] px-3 py-1 uppercase"
                onClick={() => setSlideShowToggle(!slideShowToggle)}
              >
                {!slideShowToggle ? (
                  <>
                    <PlaySvg className="mr-1 h-3 w-3" /> Start Slideshow
                  </>
                ) : (
                  <>
                    <StopSvg className="mr-1 h-5 w-5" /> Stop Slideshow
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default connect(({ whiteLabel }: RootStateTypeExtra) => ({
  propertyViewWhiteLabel: mapWhiteLabelsWithDefaults(whiteLabel).propertyView,
}))(TopControlBar)
